import { Modal, Stack, Checkbox, Badge, Button, Flex } from "@mantine/core";
import { IconRestore, IconFilterFilled } from "@tabler/icons-react";
import { useTheme } from "../contexts/ThemeContext";

interface FilterModalProps {
  opened: boolean;
  onClose: () => void;
  options: string[];
  selectedOptions: string[];
  onToggle: (option: string, checked: boolean) => void;
  onReset?: () => void;
  getBadgeProps?: (option: string) => {
    color: string;
    variant?: string;
    size?: string;
    textColor?: string;
    text?: string;
  };
}

export const FilterModal = ({
  opened,
  onClose,
  options,
  selectedOptions,
  onToggle,
  onReset,
  getBadgeProps,
}: FilterModalProps) => {
  const { theme } = useTheme();
  const themeColor = theme === "green" ? "nvidiaGreen" : "amdRed";

  const defaultBadgeProps = (option: string) => ({
    color: "gray",
    variant: "light" as const,
    size: "lg",
    text: option,
    textColor: undefined,
  });

  const getProps = getBadgeProps || defaultBadgeProps;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={280}
      centered
      padding="xl"
      transitionProps={{
        transition: "fade",
        duration: 150,
        exitDuration: 150,
      }}
      overlayProps={{
        opacity: 0.5,
        blur: 2,
      }}
      styles={{
        content: {
          background: `
            radial-gradient(circle at 20% 30%, var(--theme-overlay-20) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, var(--theme-overlay-15) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, var(--theme-overlay-12) 0%, transparent 70%),
            var(--theme-bg-modal)
          `,
          boxShadow:
            "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--theme-border)",
        },
        body: {
          background: "transparent",
        },
      }}
    >
      <Stack gap="md">
        <Flex justify="space-between" align="center" mb="xs">
          <IconFilterFilled size={20} color="white" />
          {onReset && (
            <Button
              leftSection={<IconRestore size={16} />}
              variant="light"
              color={themeColor}
              size="xs"
              onClick={onReset}
            >
              Reset
            </Button>
          )}
        </Flex>
        <Stack gap="md" mt={options.length > 4 ? "xs" : undefined}>
          {options.map((option) => {
            const props = getProps(option);
            return (
              <Checkbox
                key={option}
                color={themeColor}
                size="md"
                label={
                  <Badge
                    color={props.color}
                    variant={props.variant || "light"}
                    size={props.size || "lg"}
                    c={props.textColor}
                    style={{ verticalAlign: "middle", marginTop: "-2px" }}
                  >
                    {props.text || option}
                  </Badge>
                }
                checked={selectedOptions.includes(option)}
                onChange={(event) => onToggle(option, event.currentTarget.checked)}
                styles={{
                  input: {
                    transition: "all 0.3s ease",
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    "&:checked": {
                      backgroundColor: "var(--theme-primary)",
                      borderColor: "var(--theme-primary)",
                    },
                  },
                  body: { alignItems: "center" },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Modal>
  );
};
